import { Clock, RotateCcw, Shield, Truck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import type { StorefrontProduct } from '@/features/storefront/api/storefront';
import { RecommendedForYou } from '@/features/storefront/account/components/RecommendedForYou';
import { RelatedProducts } from '@/features/storefront/components/RelatedProducts';
import { RatingLink } from '@/features/storefront/components/StarRating';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontFeatures } from '@/features/storefront/features';
import {
  ProductDescription,
  ProductGallery,
  ProductMeta,
} from '@/features/storefront/layouts/product/ProductParts';
import {
  optionAxes,
  swatchFor,
  variantFor,
  type OptionAxis,
} from '@/features/storefront/layouts/product/options';
import { usePartsEnvironment, useProductParts } from '@/features/storefront/parts/context';
import { notifyMeTakesOver } from '@/features/storefront/parts/notifyMe';
import { partIsWide, partOwnsSpacing, type PartPresentation } from '@/features/storefront/parts/style';
import { useStorefrontPaths } from '@/features/storefront/paths';
import {
  partChoice,
  partFlag,
  partNumber,
  type PagePart,
} from '@/features/storefront/sections/parts';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * The product page, one component at a time.
 *
 * Every one of these was a paragraph inside `BuyBox` an hour ago, and the
 * markup is deliberately the same markup: the classes, the CSS variables and
 * the conditions are transcribed rather than redesigned, so a shop that has
 * never opened the component tree renders exactly the page it rendered before
 * the tree existed. What each one gained is an identity - it can be hidden,
 * moved into the gallery column, restyled, or thrown away.
 *
 * The margins are the interesting part of that promise. Each component keeps
 * the margin it always had and applies it only while {@link partOwnsSpacing} is
 * true, so an untouched page has the same rhythm to the pixel and a merchant
 * who chooses a spacing gets theirs instead of both.
 *
 * The data all arrives through {@link useProductParts}: one product, one
 * chosen variant, one quantity and the two mutations. A part rendered without
 * it draws nothing, which is what a document copied onto a page with no product
 * on it should do.
 */

export interface ProductPartProps {
  part: PagePart;
  presentation?: PartPresentation;
}

/** The size the theme wanted for a heading, where the merchant has not chosen. */
function ownClass(part: PagePart, fallback: string): string | undefined {
  return partOwnsSpacing(part) ? fallback : undefined;
}

export function ProductBrandPart(_: ProductPartProps) {
  const product = useProductParts();
  if (!product?.detail.brand) return null;
  // No classes of its own: the size, the case and the colour are the wrapper's,
  // or the inspector's controls for them would be beaten by this element and do
  // nothing. See `parts/style.ts`. No margin either - the brand has always been
  // the first line of the buy column, and the title's `mt-1` is the gap.
  return <p>{product.detail.brand}</p>;
}

export function ProductTitlePart({ part }: ProductPartProps) {
  const product = useProductParts();
  if (!product) return null;
  // The size is the wrapper's - a theme's own class string while the merchant
  // has not chosen one, theirs the moment they do. See `parts/style.ts`.
  return (
    <h1 className={cn(ownClass(part, 'mt-1'), 'break-words')} style={STOREFRONT_HEADING_STYLE}>
      {product.detail.name}
    </h1>
  );
}

/**
 * The price, and - unless the merchant said otherwise - the was-price beside
 * it.
 *
 * One part rather than two by default, because a struck-through price is read
 * *against* the price next to it: split onto two lines it stops being a
 * comparison and becomes two numbers. The merchant who wants it elsewhere adds
 * {@link ProductComparePricePart}, which is the same number on its own.
 */
export function ProductPricePart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  const environment = usePartsEnvironment();
  if (!product) return null;

  const { detail, selected } = product;
  // A page that also carries the was-price as a component of its own shows it
  // there: beside the price as well, it read as the same number twice.
  const ownWasPrice = environment?.parts.some((each) => each.type === 'product_compare_price' && each.visible);
  const price = Number(selected?.price ?? detail.price);
  const compareAt = detail.compare_at_price ? Number(detail.compare_at_price) : null;
  const saving = compareAt && compareAt > price ? Math.round((1 - price / compareAt) * 100) : null;

  return (
    <div className={cn(ownClass(part, 'mt-4'), 'flex flex-wrap items-baseline gap-3')}>
      {/* The price takes the wrapper's type. The was-price beside it keeps its
          own smaller size and its own muted colour on purpose: it is read
          *against* the price, so it stays one step quieter whatever size the
          merchant sets the pair to. */}
      <span>{formatMoney(selected?.price ?? detail.price)}</span>
      {detail.compare_at_price && partFlag(part, 'show_compare_at') && !ownWasPrice ? (
        <span className="text-lg font-normal line-through" style={{ color: 'var(--storefront-muted)' }}>
          {formatMoney(detail.compare_at_price)}
        </span>
      ) : null}
      {saving !== null && partFlag(part, 'show_saving') ? (
        <span className="text-sm font-semibold" style={{ color: 'var(--storefront-primary)' }}>
          {text('product_page.save_percent', { percent: saving })}
        </span>
      ) : null}
      {/* Its own size, like the was-price: the stars are read beside the
          price, never as loud as it. */}
      <RatingLink average={detail.rating_average} count={detail.rating_count} className="self-center" />
    </div>
  );
}

export function ProductComparePricePart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  if (!product?.detail.compare_at_price) return null;
  const word = part.label.trim() || text('product_page.was');

  return (
    <p className={ownClass(part, 'mt-1')}>
      {partFlag(part, 'show_label') ? <span className="me-1.5">{word}</span> : null}
      <span className="line-through">{formatMoney(product.detail.compare_at_price)}</span>
    </p>
  );
}

export function ProductTaxNotePart({ part }: ProductPartProps) {
  const text = useShopContent();
  return (
    <p className={ownClass(part, 'mt-1')}>{text('product.tax_note')}</p>
  );
}

/**
 * The chosen variant's code.
 *
 * Only when there is one to show: a shop whose products have a single
 * auto-generated variant has an SKU nobody types, and a line reading "SKU:" with
 * nothing after it is worse than no line.
 */
export function ProductSkuPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  const sku = product?.selected?.sku?.trim();
  if (!sku) return null;
  const word = part.label.trim() || text('product_page.sku');

  return (
    <p className={ownClass(part, 'mt-2')}>
      {word} {sku}
    </p>
  );
}

export function ProductAvailabilityPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  if (!product) return null;

  const inStock = product.selected?.in_stock ?? false;
  const own = partChoice(part, inStock ? 'in_stock_text' : 'out_of_stock_text').trim();
  const words = own || (inStock ? text('product_page.in_stock') : text('product.out_of_stock'));

  return (
    <p className={cn(ownClass(part, 'mt-2'), 'flex items-center gap-2')}>
      {partFlag(part, 'show_dot') ? (
        <span
          aria-hidden
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: inStock ? '#16a34a' : 'var(--storefront-muted)' }}
        />
      ) : null}
      {words}
    </p>
  );
}

export function ProductShortDescriptionPart({ part }: ProductPartProps) {
  const product = useProductParts();
  if (!product?.detail.short_description) return null;
  return (
    <p className={ownClass(part, 'mt-5')}>{product.detail.short_description}</p>
  );
}

/**
 * One axis of the choice: the fabrics as dots, the sizes as words.
 *
 * The API hands over the *combinations* - six rows for three fabrics in two
 * sizes - and one flat row of six buttons reading "Sage Cotton / 3 seat" is a
 * list to decode rather than a choice to make. This is the other arrangement: a
 * row per axis, in the order the merchant declared them.
 *
 * Whether a row wears swatches is decided by the *values* rather than by the
 * axis's name - see `optionAxes` - so a merchant who calls their colour axis
 * "Shade" still gets dots, and one who puts sizes in a column called "Colour"
 * gets readable buttons instead of identical grey circles.
 */
function OptionAxisRow({
  axis,
  detail,
  selected,
  onSelectVariant,
  swatches,
  hideSoldOut,
}: {
  axis: OptionAxis;
  detail: StorefrontProduct;
  selected: StorefrontProduct['variants'][number] | undefined;
  onSelectVariant: (id: string) => void;
  swatches: boolean;
  hideSoldOut: boolean;
}) {
  const chosen = String(selected?.options[axis.name] ?? '');
  const values = hideSoldOut
    ? axis.values.filter((value) => variantFor(detail.variants, selected, axis.name, value)?.in_stock)
    : axis.values;
  if (values.length === 0) return null;

  return (
    <div className="mt-6 first:mt-0">
      <p className="text-sm font-semibold">
        {axis.name}
        {chosen ? (
          <span className="ms-1 font-normal" style={{ color: 'var(--storefront-muted)' }}>
            {chosen}
          </span>
        ) : null}
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2.5">
        {values.map((value) => {
          const variant = variantFor(detail.variants, selected, axis.name, value);
          const isChosen = value === chosen;
          // A value with no variant behind it cannot be reached from here at
          // all; one whose variant is out of stock can be chosen and then reads
          // "out of stock" on the button, which is the honest order.
          const disabled = !variant;

          if (swatches) {
            return (
              <button
                key={value}
                type="button"
                aria-label={value}
                aria-pressed={isChosen}
                title={value}
                disabled={disabled}
                onClick={() => variant && onSelectVariant(variant.id)}
                className={cn(
                  'grid size-10 place-items-center rounded-full border-2 transition disabled:cursor-not-allowed disabled:opacity-40',
                  variant && !variant.in_stock && 'opacity-45',
                )}
                style={{ borderColor: isChosen ? 'var(--storefront-text)' : 'transparent' }}
              >
                <span
                  aria-hidden
                  className="size-7 rounded-full border"
                  style={{ backgroundColor: swatchFor(value), borderColor: 'rgba(0,0,0,0.14)' }}
                />
              </button>
            );
          }

          return (
            <button
              key={value}
              type="button"
              aria-pressed={isChosen}
              disabled={disabled}
              onClick={() => variant && onSelectVariant(variant.id)}
              className={cn(
                'inline-flex h-10 items-center border px-5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40',
                variant && !variant.in_stock && 'line-through opacity-50',
              )}
              style={{
                borderRadius: 'var(--storefront-radius)',
                borderColor: isChosen ? 'var(--storefront-text)' : 'var(--storefront-surface)',
                backgroundColor: isChosen ? 'var(--storefront-surface)' : undefined,
              }}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The choice a shopper makes before buying.
 *
 * Renders nothing at all when the product has no options, exactly as the buy
 * box always did: a lone "Standard" button beside a product with one variant is
 * a decision the shopper does not have.
 */
export function ProductVariantsPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  if (!product) return null;

  const { detail, selected, onSelectVariant } = product;
  const hasOptions = detail.variants.some((variant) => Object.keys(variant.options).length > 0);
  if (!hasOptions) return null;

  const label = (variant: (typeof detail.variants)[number]) =>
    Object.values(variant.options).join(' / ') || text('product.option_standard');
  const offered = partFlag(part, 'hide_sold_out')
    ? detail.variants.filter((variant) => variant.in_stock)
    : detail.variants;
  const heading = part.label.trim() || text('product.options');
  const style = partChoice(part, 'style');

  // A row per axis rather than one flat row of combinations. The API hands over
  // the combinations - six rows for three fabrics in two sizes - and six buttons
  // reading "Sage Cotton / 3 seat" is a list to decode rather than a choice to
  // make. `swatches` draws the colour axes as dots; the rest stay words.
  if (style === 'axes' || style === 'swatches') {
    const axes = optionAxes(detail.variants);
    if (axes.length === 0) return null;
    return (
      <div className={ownClass(part, 'mt-6')}>
        {axes.map((axis) => (
          <OptionAxisRow
            key={axis.name}
            axis={axis}
            detail={detail}
            selected={selected}
            onSelectVariant={onSelectVariant}
            swatches={style === 'swatches' && axis.swatches}
            hideSoldOut={partFlag(part, 'hide_sold_out')}
          />
        ))}
      </div>
    );
  }

  if (style === 'dropdown') {
    return (
      <div className={ownClass(part, 'mt-6')}>
        <label className="mb-2 block text-sm font-medium" htmlFor={`variants-${part.id}`}>
          {heading}
        </label>
        <select
          id={`variants-${part.id}`}
          value={selected?.id ?? ''}
          onChange={(event) => onSelectVariant(event.target.value)}
          className="h-11 w-full border px-3 text-sm"
          style={{
            borderColor: 'var(--storefront-surface)',
            borderRadius: 'var(--storefront-radius)',
          }}
        >
          {offered.map((variant) => (
            <option key={variant.id} value={variant.id} disabled={!variant.in_stock}>
              {label(variant)}
              {variant.in_stock ? '' : ` - ${text('product.out_of_stock')}`}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={ownClass(part, 'mt-6')}>
      <p className="mb-2 text-sm font-medium">{heading}</p>
      <div className="flex flex-wrap gap-2">
        {offered.map((variant) => {
          const isSelected = variant.id === selected?.id;
          return (
            <button
              key={variant.id}
              type="button"
              disabled={!variant.in_stock}
              onClick={() => onSelectVariant(variant.id)}
              className="border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                borderRadius: 'var(--storefront-radius)',
                borderColor: isSelected ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
                color: isSelected ? 'var(--storefront-primary)' : undefined,
              }}
            >
              {label(variant)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * How many, for a shop where people buy more than one.
 *
 * The number it holds is the page's, not this component's - see
 * `ProductPartsData.quantity`. A merchant who deletes this part has a page that
 * adds one at a time again, because that is what the number falls back to.
 */
export function ProductQuantityPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  if (!product) return null;

  const max = partNumber(part, 'max') || 10;
  const heading = part.label.trim() || text('product.quantity');

  return (
    <div className={ownClass(part, 'mt-6')}>
      <label className="mb-2 block text-sm font-medium" htmlFor={`quantity-${part.id}`}>
        {heading}
      </label>
      <div className="inline-flex items-center">
        <button
          type="button"
          aria-label={text('product_page.quantity_decrease')}
          disabled={product.quantity <= 1}
          onClick={() => product.setQuantity(product.quantity - 1)}
          className="h-11 w-11 border text-lg disabled:opacity-40"
          style={{
            borderColor: 'var(--storefront-surface)',
            borderRadius: 'var(--storefront-radius) 0 0 var(--storefront-radius)',
          }}
        >
          &minus;
        </button>
        <input
          id={`quantity-${part.id}`}
          type="number"
          min={1}
          max={max}
          value={product.quantity}
          onChange={(event) => product.setQuantity(Number(event.target.value))}
          className="h-11 w-16 border-y text-center text-sm"
          style={{ borderColor: 'var(--storefront-surface)' }}
        />
        <button
          type="button"
          aria-label={text('product_page.quantity_increase')}
          disabled={product.quantity >= max}
          onClick={() => product.setQuantity(product.quantity + 1)}
          className="h-11 w-11 border text-lg disabled:opacity-40"
          style={{
            borderColor: 'var(--storefront-surface)',
            borderRadius: '0 var(--storefront-radius) var(--storefront-radius) 0',
          }}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * The button, and what stands where it would be on a shop that does not sell.
 *
 * The enquiry path is the one `BuyBox` already had: never disabled and never
 * conditional on stock, because a consultation has no stock and the ordinary
 * "Out of stock" path would put a dead grey button on every service a clinic
 * offers.
 */
export function ProductAddToCartPart({ part, presentation }: ProductPartProps) {
  const text = useShopContent();
  const paths = useStorefrontPaths();
  const { cart: sellsThings, enquiry } = useStorefrontFeatures();
  const product = useProductParts();
  const environment = usePartsEnvironment();
  if (!product) return null;

  const wide = partIsWide(part, presentation);
  const own = part.label.trim();

  // Sold out, with a "Notify me" box on the page standing in for the button.
  if (
    sellsThings &&
    product.selected &&
    !product.selected.in_stock &&
    !environment?.includeHidden &&
    notifyMeTakesOver(environment?.parts)
  ) {
    return null;
  }

  if (!sellsThings) {
    return (
      <Link
        to={paths.menu(enquiry.target)}
        className={cn(
          ownClass(part, 'mt-8'),
          'inline-flex h-12 items-center justify-center px-7 text-sm font-semibold text-white',
          wide ? 'w-full' : 'w-full sm:w-auto',
        )}
        style={{
          backgroundColor: 'var(--storefront-primary)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        {own || enquiry.label.trim() || text('product.enquire')}
      </Link>
    );
  }

  const inStock = product.selected?.in_stock ?? false;
  return (
    <button
      type="button"
      disabled={!inStock || product.adding}
      onClick={() => product.onAddToCart(product.quantity)}
      className={cn(
        ownClass(part, 'mt-8'),
        'inline-flex h-12 items-center justify-center px-7 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50',
        wide ? 'w-full' : 'w-full sm:w-auto',
      )}
      style={{
        backgroundColor: 'var(--storefront-primary)',
        borderRadius: 'var(--storefront-radius)',
      }}
    >
      {!inStock
        ? text('product.out_of_stock')
        : product.adding
          ? text('product.adding')
          : own || text('product.add_to_cart')}
    </button>
  );
}

/** Straight to the till. Absent on a shop with no till, like the button above. */
export function ProductBuyNowPart({ part, presentation }: ProductPartProps) {
  const text = useShopContent();
  const features = useStorefrontFeatures();
  const product = useProductParts();
  if (!product || !features.cart || !features.checkout) return null;

  const solid = partChoice(part, 'style') === 'solid';
  const wide = partIsWide(part, presentation);
  const inStock = product.selected?.in_stock ?? false;

  return (
    <button
      type="button"
      disabled={!inStock || product.adding}
      onClick={() => product.onBuyNow(product.quantity)}
      className={cn(
        ownClass(part, 'mt-3'),
        'inline-flex h-12 items-center justify-center border px-7 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50',
        wide ? 'w-full' : 'w-full sm:w-auto',
      )}
      style={{
        backgroundColor: solid ? 'var(--storefront-primary)' : 'transparent',
        color: solid ? '#fff' : 'var(--storefront-primary)',
        borderColor: 'var(--storefront-primary)',
        borderRadius: 'var(--storefront-radius)',
      }}
    >
      {part.label.trim() || text('product.buy_now')}
    </button>
  );
}

export function ProductSpecsPart({ part }: ProductPartProps) {
  const product = useProductParts();
  if (!product) return null;
  return (
    <ProductMeta
      detail={product.detail}
      heading={part.label.trim() || undefined}
      className={partOwnsSpacing(part) ? undefined : ''}
    />
  );
}

export function ProductDescriptionPart({ part }: ProductPartProps) {
  const product = useProductParts();
  if (!product) return null;

  return (
    <ProductDescription
      detail={product.detail}
      heading={part.label.trim() || undefined}
      showHeading={partFlag(part, 'show_heading') || undefined}
      collapsible={partFlag(part, 'collapsible')}
      className={partOwnsSpacing(part) ? undefined : ''}
    />
  );
}

const SHIPPING_ICONS: Record<string, typeof Truck> = {
  truck: Truck,
  return: RotateCcw,
  shield: Shield,
  clock: Clock,
};

/**
 * What the shop promises about getting it there and about sending it back.
 *
 * The merchant's own words, because they are the merchant's own policy: this
 * part reads nothing from the order, quotes no rate and makes no promise the
 * shop has not typed. Folded away by default, because it is reassurance rather
 * than something a shopper came to read.
 */
export function ProductShippingInfoPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const [open, setOpen] = useState(false);
  const body = part.body.trim();
  const heading = part.label.trim() || text('product_page.delivery_returns');
  if (!body) return null;

  const Icon = SHIPPING_ICONS[partChoice(part, 'icon')];
  const head = (
    <span className="flex items-center gap-2 text-sm font-semibold">
      {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
      {heading}
    </span>
  );

  if (!partFlag(part, 'collapsible')) {
    return (
      <div className={cn(ownClass(part, 'mt-6'), 'border-t pt-5')} style={{ borderColor: 'var(--storefront-surface)' }}>
        {head}
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed" style={{ color: 'var(--storefront-muted)' }}>
          {body}
        </p>
      </div>
    );
  }

  return (
    <div className={cn(ownClass(part, 'mt-6'), 'border-t pt-5')} style={{ borderColor: 'var(--storefront-surface)' }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center justify-between gap-3 text-start"
      >
        {head}
        <span aria-hidden className="text-lg leading-none">
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? (
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed" style={{ color: 'var(--storefront-muted)' }}>
          {body}
        </p>
      ) : null}
    </div>
  );
}

export function ProductGalleryPart({ part, presentation }: ProductPartProps) {
  const product = useProductParts();
  if (!product) return null;

  const aspect = partChoice(part, 'aspect');
  const thumbs = partChoice(part, 'thumbs');
  const style = partChoice(part, 'style');
  const themed = presentation?.gallery;
  // The layout decides where the strip goes, except "carousel" - today's
  // gallery - which keeps reading the thumbnails setting and the theme's own.
  const strip =
    style === 'thumbs_left'
      ? 'rail'
      : style === 'thumbs_bottom'
        ? 'below'
        : thumbs === 'theme'
          ? themed?.thumbs
          : (thumbs as 'below' | 'rail');

  return (
    <ProductGallery
      detail={product.detail}
      aspect={aspect === 'theme' ? themed?.aspect : (aspect as 'square' | 'portrait' | 'wide')}
      thumbs={strip}
      rounded={partFlag(part, 'rounded') && (themed?.rounded ?? true)}
      layout={style === 'grid' ? 'grid' : 'single'}
    />
  );
}

export function ProductRelatedPart({ part }: ProductPartProps) {
  const product = useProductParts();
  if (!product) return null;
  return <RelatedProducts slug={product.detail.slug} className={ownClass(part, 'mt-14')} />;
}

export function ProductRecommendationsPart({ part }: ProductPartProps) {
  return <RecommendedForYou className={ownClass(part, 'mt-14')} headingLevel="h2" />;
}
